import axios from 'axios'
import memoize from 'memoizee'
import RealTimeEvents, { RealtimeEvent } from './RealTimeEvents'
import { APIConfig } from 'types/definitions'
import { switchMap } from 'rxjs/operators'
import { Observable } from 'rxjs'

/**
 * Resolves a path-based base URL against the current origin.
 * Absolute base URLs are returned as-is.
 *
 * @param baseURL
 */
export function resolveBaseURL(baseURL: string): string {
  return baseURL.startsWith('/')
    ? `${window.location.origin}${baseURL}`
    : baseURL
}

/**
 * Memoized event manager factory.
 */
const getEventsManager = memoize(function getEventsManager(
  host: string,
  token: string,
  globalParams?: Record<string, unknown>,
) {
  return new RealTimeEvents().connect(host, token, globalParams)
})

/**
 * API client base.
 */
export class APIClient {
  config: APIConfig
  token: string | null = null

  /**
   * Axios client.
   */
  readonly client

  /**
   * Constructor.
   *
   * @param {Function} config.getToken
   * @param {string} config.baseURL
   */
  constructor(config: APIConfig) {
    if (!config) {
      throw new Error('No config given.')
    }

    if (!config.baseURL) {
      throw new Error('No config.baseURL given.')
    }

    this.config = config

    this.config.token$.subscribe((token) => (this.token = token))
    this.client = axios.create({
      baseURL: resolveBaseURL(this.config.baseURL),
    })
    this.client.interceptors.request.use((config) => {
      const token = this.token
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      if (this.config.globalParams) {
        config.params ??= {}
        Object.assign(config.params, this.config.globalParams)
      }
      return config
    })
  }

  /**
   * Emits every realtime event on the connection.
   * Don't call this until we have a token.
   */
  events<T extends RealtimeEvent>() {
    return createRealtimeEvents<T>(this.config)
  }

  /**
   * Don't call this until we have a token.
   */
  when<T extends RealtimeEvent>(event: T['type']) {
    return this.config.token$.pipe(
      switchMap(
        (token) =>
          new Observable<T>((observer) => {
            // `getEventsManager` caches based on host and token, so we don't need
            // to do any caching here.
            const manager = getEventsManager(
              this.client.defaults.baseURL!,
              token,
              this.config.globalParams,
            )
            return manager.subscribe<T>(event, (e) => observer.next(e))
          }),
      ),
    )
  }

  /**
   * Helper to extract data.
   */
  _data<T>(resp: { data: T }) {
    return resp.data
  }
}

/**
 * Creates an observable of every realtime event on the connection
 * described by the given config. Don't subscribe until there is
 * a token.
 */
export function createRealtimeEvents<T extends RealtimeEvent>(
  config: APIConfig,
): Observable<T> {
  return config.token$.pipe(
    switchMap(
      (token) =>
        new Observable<T>((observer) => {
          // `getEventsManager` caches based on host and token, so we don't need
          // to do any caching here.
          const manager = getEventsManager(
            resolveBaseURL(config.baseURL),
            token,
            config.globalParams,
          )
          return manager.on('event', (e) => observer.next(e as T))
        }),
    ),
  )
}

/**
 * Options for `sendRequestWithUploadProgress`.
 */
export interface UploadProgressRequestOptions {
  baseURL: string
  path: string
  method: string
  headers: Record<string, string>
  body: FormData
  /**
   * Reports the upload progress.
   */
  onProgress: (e: { loaded: number; total: number }) => void
}

/**
 * Sends a request through axios so the upload progress can be
 * reported, which `fetch` cannot provide.
 */
export async function sendRequestWithUploadProgress<T>(
  options: UploadProgressRequestOptions,
): Promise<T> {
  const response = await axios.request<T>({
    baseURL: options.baseURL,
    url: options.path,
    method: options.method,
    headers: options.headers,
    data: options.body,
    onUploadProgress: (e) => {
      options.onProgress({
        loaded: e.loaded,
        total: e.total ?? e.loaded,
      })
    },
  })

  return response.data
}
